import { Component } from '@angular/core';
import { AuthService } from '../../services/auth.service';
import { SurveyService } from '../../services/survey.service';
import { firstValueFrom } from 'rxjs';
import { User } from '../../models/User';
import {
    SurveyQuestion,
    SurveyQuestionType,
} from '../../models/SurveyQuestion';

import { FormsModule } from '@angular/forms';
import { SurveyAnswer } from '../../models/SurveyAnswer';
import { Router } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';

@Component({
    selector: 'app-survey',
    standalone: true,
    imports: [FormsModule, TranslateModule],
    templateUrl: './survey.component.html',
    styleUrl: './survey.component.scss',
})
export class SurveyComponent {
    SurveyQuestionType = SurveyQuestionType;
    user: User;
    survey: SurveyQuestion[];
    answers: (string | string[])[] = [];

    constructor(
        private router: Router,
        private authService: AuthService,
        private surveyService: SurveyService
    ) {}

    async ngOnInit() {
        const user = await firstValueFrom(
            this.authService.getUserInfoOrRefreshIfNeeded()
        );
        const survey = await firstValueFrom(
            this.surveyService.getSurveyQuestions()
        );

        const answers = survey.map((elt) => {
            switch (elt.question_survey_type) {
                case SurveyQuestionType.onePick:
                    return [];
                case SurveyQuestionType.threeOrderedPicks:
                    return [];
                case SurveyQuestionType.openAnswer:
                    return '';
                default:
                    return '';
            }
        });

        this.user = user;
        this.answers = answers;
        this.survey = survey;
    }

    selectAnswerForQuestion(answer: string, question: SurveyQuestion) {
        const index = this.survey.findIndex(
            (elt) => elt.question_survey_id === question.question_survey_id
        );

        if (index === -1) {
            return;
        }

        switch (question.question_survey_type) {
            case SurveyQuestionType.onePick:
                this.answers[index] = [answer];
                break;
            case SurveyQuestionType.threeOrderedPicks:
                const answerIndex = (this.answers[index] as string[]).findIndex(
                    (elt) => elt === answer
                );

                if (answerIndex === -1) {
                    if (this.answers[index].length === 3) {
                        return;
                    } else {
                        (this.answers[index] as string[]).push(answer);
                    }
                } else {
                    (this.answers[index] as string[]).splice(answerIndex, 1);
                }
                break;
            default:
                break;
        }
    }

    isActive(question: SurveyQuestion, answer: string): boolean {
        const index = this.survey.findIndex(
            (elt) => elt.question_survey_id === question.question_survey_id
        );

        if (index === -1) {
            return false;
        }

        const answerIndex = (this.answers[index] as string[]).findIndex(
            (elt) => elt === answer
        );

        return answerIndex !== -1;
    }

    areAllAnswersOk(): boolean {
        return this.answers.findIndex((elt) => elt.length === 0) === -1;
    }

    async sendAnswers() {
        if (!this.areAllAnswersOk()) {
            return;
        }

        let array: SurveyAnswer[] = [];

        for (let i = 0; i < this.survey.length; i++) {
            const question = this.survey[i];
            const answer = this.answers[i];
            const obj = new SurveyAnswer(
                this.user.id,
                question.question_survey_id,
                true,
                Array.isArray(answer) ? answer : [answer]
            );

            array.push(obj);
        }

        await firstValueFrom(this.surveyService.postSurveyAnswers(array));
        await firstValueFrom(this.authService.forceUpdateUser());
        this.router.navigate(['homec']);
    }
}
